import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { AuthHeader, AuthShell } from '@/components/AuthShell';

/**
 * Desktop OAuth callback bridge.
 *
 * Why this exists: the desktop app uses `fluxter://callback` as its
 * deep-link target. When WorkOS / Google redirected the system
 * browser there directly, the OS would intercept the protocol and
 * launch Fluxter, but the browser tab was left mid-navigation on a
 * URL it can't render — so the Google account picker (or whatever
 * was loading just before) sat spinning forever. Bad UX, looks
 * broken even though sign-in worked.
 *
 * Fix: WorkOS now redirects to this HTTPS page at
 * `https://fluxter.app/auth/desktop-bridge?code=…`. We immediately
 * trigger the deep link via a JS navigation, which the OS handles
 * just like the original direct redirect, but the browser tab stays
 * loaded on this proper HTTPS page rendering a clean
 * "Sign-in complete — you can close this tab" message.
 *
 * If somebody lands here in a regular browser (no protocol handler
 * registered for `fluxter://`, e.g. opened on a phone or after
 * uninstalling the desktop app), the deep-link redirect is a no-op
 * and they just see the message — informative rather than broken.
 */
export const DesktopAuthBridgePage = () => {
  const triggered = useRef(false);
  const [error, setError] = useState<{ code: string; description: string } | null>(null);
  const [missingCode, setMissingCode] = useState(false);

  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    // WorkOS / Google can come back with `error` + `error_description`
    // instead of `code` (e.g. user denied consent). Forward those to
    // the desktop app the same way — `auth.ts` handles them — and
    // surface a friendlier error inline here so the user knows what
    // happened without bouncing back to the desktop window.
    const errorCode = params.get('error');
    const errorDescription = params.get('error_description');
    const code = params.get('code');

    if (errorCode || errorDescription) {
      setError({
        code: errorCode ?? 'unknown_error',
        description: errorDescription ?? 'Sign-in was cancelled or failed.',
      });
    } else if (!code) {
      // Nothing useful to forward — tell the user to retry from the
      // desktop app rather than silently doing nothing.
      setMissingCode(true);
      return;
    }

    // Forward all original query params to the protocol handler so
    // the desktop app sees the same shape WorkOS would have sent
    // directly. The OS / Electron deep-link handler picks it up.
    window.location.href = `fluxter://callback?${params.toString()}`;
  }, []);

  if (error) {
    return (
      <AuthShell
        width="md"
        header={
          <AuthHeader
            title="Sign-in didn’t complete"
            subtitle="Return to Fluxter and try again — no harm done."
          />
        }
      >
        <div className="border-border bg-card flex items-start gap-3 rounded-lg border p-4">
          <AlertTriangle
            className="text-destructive mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <div className="space-y-0.5">
            <p className="text-foreground text-[12.5px] font-medium">{error.code}</p>
            <p className="text-foreground-muted text-[12px] leading-relaxed">
              {error.description}
            </p>
          </div>
        </div>
        <p className="text-foreground-faint mt-5 text-center text-[11px]">
          You can close this tab.
        </p>
      </AuthShell>
    );
  }

  if (missingCode) {
    return (
      <AuthShell
        width="md"
        header={
          <AuthHeader
            title="Nothing to do here"
            subtitle="This page is the desktop sign-in handoff. Open Fluxter to sign in."
          />
        }
      >
        <p className="text-foreground-faint text-center text-[11px]">
          You can close this tab.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      width="md"
      header={
        <AuthHeader
          title="You’re signed in"
          subtitle="Switching back to Fluxter — you can close this tab."
        />
      }
    >
      <div className="border-border bg-card flex items-center gap-3 rounded-lg border p-4">
        <CheckCircle2
          className="text-foreground-muted size-4 shrink-0"
          aria-hidden="true"
        />
        <p className="text-foreground text-[12.5px]">
          Sign-in complete. Return to the Fluxter app to continue.
        </p>
      </div>
    </AuthShell>
  );
};
